import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import * as chatbotApi from '../api/chatbot';
import { connectChatSocket } from '../api/chatSocket';
import { readConversationId, writeConversationId, clearConversationId } from '../storage/chat';
import { useCart } from './CartContext';

// Chat bot (internal_docs/chat/chat_bot.md §3.2). Mirrors the shape of
// CartContext: state here, mounted once (gated on auth — see Layout.js,
// chat has no guest mode per chat_bot.md §4), talks only to src/api/chatbot.js.
const ChatContext = createContext(null);

const DAILY_LIMIT_REACHED_CODE = 'DAILY_LIMIT_REACHED';

// No `status === 429` check here on purpose: the widget only ever sends via
// the SSE path (streamMessage below), whose `error` event is synthesized
// client-side from the SSE payload alone (see onError) and never carries an
// HTTP status — the server-side SSE route forwards only `err.message`, not
// `err.code`, once headers have already committed to 200. The `code` field
// inside the payload is the only reliable signal.
const isDailyLimitError = (err) => err?.response?.data?.code === DAILY_LIMIT_REACHED_CODE;

export function ChatProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [dailyMessageLimit, setDailyMessageLimit] = useState(null);
  const [dailyLimitReached, setDailyLimitReached] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [sending, setSending] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [activeToolCalls, setActiveToolCalls] = useState(0);
  const [conversationMode, setConversationMode] = useState('bot');
  const socketRef = useRef(null);
  const conversationIdRef = useRef(null);
  const { refresh: refreshCart } = useCart();

  // appends a message delivered over the socket (Phase 8 human handoff) —
  // deduped by _id, since the tab that originated a bot reply also gets it
  // via its own streamMessage completion (see onMessage below) and the two
  // can arrive in either order. The server now also emits the customer's own
  // userMessage (so the staff console sees it live), which reaches this same
  // tab well before its streamMessage call finishes — swap it into the
  // still-pending optimistic placeholder rather than showing both side by
  // side for the whole reply.
  const appendSocketMessage = useCallback((message) => {
    setMessages((prev) => {
      if (prev.some((m) => m._id === message._id)) return prev;
      if (message.role === 'user') {
        const pendingIndex = prev.findIndex((m) => m.pending && m.role === 'user' && m.content === message.content);
        if (pendingIndex !== -1) {
          const next = [...prev];
          next[pendingIndex] = message;
          return next;
        }
      }
      return [...prev, message];
    });
  }, []);

  // Realtime delivery (chat_bot_implementation.md Phase 8): one socket for
  // the life of this provider (mounted only for a logged-in user), joined to
  // whichever conversation room is active. Covers a staff reply arriving
  // outside this tab's own request/response cycle, and keeps any other open
  // tab/device on the same conversation in sync.
  useEffect(() => {
    const socket = connectChatSocket();
    socketRef.current = socket;

    socket.on('message', appendSocketMessage);
    socket.on('conversation', (conversation) => setConversationMode(conversation.mode || 'bot'));
    // staff deleted this conversation (adminDeleteConversation) — the
    // customer's own delete (clearConversation below) already resets this
    // state itself without waiting on the socket; this is for the case
    // where someone else deleted it out from under an open widget, so it
    // doesn't keep pointing at a conversation id that 404s on the next send.
    socket.on('conversationDeleted', () => {
      setMessages([]);
      setConversationId(null);
      setDailyLimitReached(false);
      setConversationMode('bot');
      clearConversationId();
    });

    // rejoin the active conversation and re-fetch its messages to pick up
    // anything sent while disconnected (chat_bot_implementation.md Phase 10).
    const resync = () => {
      const id = conversationIdRef.current;
      if (!id) return;
      socket.emit('join', id);
      chatbotApi
        .getConversation(id)
        .then(({ data }) => {
          setMessages(data.messages);
          setConversationMode(data.conversation?.mode || 'bot');
        })
        .catch(() => {
          // gone while disconnected (expired/deleted) — nothing more to sync
        });
    };

    // A reconnect (network flake, backgrounded tab) gets a new underlying
    // connection server-side, so the room join above doesn't survive it —
    // 'connect' fires again on every reconnect, not just the first one.
    let hasConnectedOnce = false;
    const onConnect = () => {
      if (!hasConnectedOnce) {
        hasConnectedOnce = true;
        return;
      }
      resync();
    };
    socket.on('connect', onConnect);

    // Backstop for the same case the 'connect' handler above is meant to
    // catch — a backgrounded tab throttles timers/reconnection, so a drop
    // that happens while this tab isn't focused can go unnoticed well past
    // when the socket would otherwise have recovered on its own. Resync
    // whenever the tab regains focus, regardless of what the socket did.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') resync();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [appendSocketMessage]);

  useEffect(() => {
    conversationIdRef.current = conversationId;
    const socket = socketRef.current;
    if (!socket || !conversationId) return undefined;

    socket.emit('join', conversationId);
    return () => socket.emit('leave', conversationId);
  }, [conversationId]);

  const openWidget = useCallback(() => setIsOpen(true), []);
  const closeWidget = useCallback(() => setIsOpen(false), []);
  const toggleWidget = useCallback(() => setIsOpen((v) => !v), []);

  // Hydrate the daily-limit copy + any conversation from a previous session,
  // once on mount (this provider only mounts for a logged-in user).
  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const { data } = await chatbotApi.getChatConfig();
        if (active) setDailyMessageLimit(data.dailyMessageLimit);
      } catch {
        // non-fatal — the limit-reached copy just won't cite a specific number
      }

      const existingId = readConversationId();
      if (existingId) {
        try {
          const { data } = await chatbotApi.getConversation(existingId);
          if (active) {
            setConversationId(existingId);
            setMessages(data.messages);
            setConversationMode(data.conversation?.mode || 'bot');
          }
        } catch {
          // gone (expired past its 24h TTL, or deleted elsewhere) — start fresh
          clearConversationId();
        }
      }

      if (active) setInitializing(false);
    })();

    return () => {
      active = false;
    };
  }, []);

  const sendMessage = useCallback(
    async (content) => {
      const trimmed = content.trim();
      if (!trimmed || sending) return;

      setSending(true);
      setStreamingContent('');
      setActiveToolCalls(0);
      const optimisticUser = {
        _id: `pending-${Date.now()}`,
        role: 'user',
        content: trimmed,
        createdAt: new Date().toISOString(),
        pending: true,
      };
      setMessages((prev) => [...prev, optimisticUser]);

      try {
        let id = conversationId;
        if (!id) {
          const { data: conversation } = await chatbotApi.createConversation();
          id = conversation._id;
          setConversationId(id);
          writeConversationId(id);
        }

        // Collected rather than thrown directly from the handler below —
        // streamMessage's event loop isn't itself in this try/catch frame.
        let streamError = null;

        await chatbotApi.streamMessage(id, trimmed, {
          onDelta: ({ text }) => setStreamingContent((prev) => prev + text),
          onToolCallStart: () => {
            // any text accumulated so far was a pre-tool-call preamble, not
            // the final answer — drop it so the tool tag isn't mixed with it
            setStreamingContent('');
            setActiveToolCalls((c) => c + 1);
          },
          onToolCallEnd: ({ name, result }) => {
            setActiveToolCalls((c) => Math.max(0, c - 1));
            // The addToCart tool mutates the server-side cart through a
            // completely separate path from CartContext's own addItem (which
            // is what normally updates this tab's cart state) — without
            // this, the cart page/badge silently goes stale after a
            // chat-driven add until the next unrelated cart action or a full
            // page reload, even though the server-side cart is correct.
            if (name === 'addToCart' && result && !result.error) {
              refreshCart().catch(() => {});
            }
          },
          onMessage: (data) => {
            // assistantMessage is null while a conversation is in human mode
            // (chatbotFunctions.sendMessage/streamMessage, Phase 7) — the bot
            // stayed silent, so there's nothing to add for it. Additions are
            // deduped by _id against appendSocketMessage above, since the two
            // can arrive in either order (see its comment).
            setMessages((prev) => {
              const additions = [data.userMessage, data.assistantMessage].filter(Boolean);
              const newIds = new Set(additions.map((m) => m._id));
              const rest = prev.filter((m) => m !== optimisticUser && !newIds.has(m._id));
              return [...rest, ...additions];
            });
          },
          onError: (data) => {
            streamError = new Error('chat stream error');
            streamError.response = { data: typeof data === 'string' ? { chatbot: data } : data };
          },
        });

        if (streamError) throw streamError;
      } catch (err) {
        setMessages((prev) => prev.filter((m) => m !== optimisticUser));
        if (isDailyLimitError(err)) {
          setDailyLimitReached(true);
        } else {
          throw err; // let the caller (ChatInput) surface it via Toast
        }
      } finally {
        setSending(false);
        setStreamingContent('');
        setActiveToolCalls(0);
      }
    },
    [conversationId, sending, refreshCart]
  );

  // Starts a brand-new conversation: deletes the current one server-side
  // (best-effort — it may already be gone/expired) and resets local state,
  // matching what the hydrate effect does when a stored id 404s.
  const clearConversation = useCallback(async () => {
    const id = conversationId;
    setMessages([]);
    setConversationId(null);
    setDailyLimitReached(false);
    setConversationMode('bot');
    clearConversationId();
    if (id) {
      try {
        await chatbotApi.deleteConversation(id);
      } catch {
        // non-fatal — local state is already reset either way
      }
    }
  }, [conversationId]);

  // Ends the current conversation without deleting it — unlike
  // clearConversation above, the transcript/token history stays intact
  // server-side for staff review (chatbotFunctions.js's closeConversation);
  // the customer just can't continue it (a follow-up message starts a brand
  // new conversation instead — see prepareTurn's archived check). Offered
  // when the user closes the widget (see ChatPanel's onCloseAttempt).
  const closeConversation = useCallback(async () => {
    const id = conversationId;
    setMessages([]);
    setConversationId(null);
    setDailyLimitReached(false);
    setConversationMode('bot');
    clearConversationId();
    if (id) {
      try {
        await chatbotApi.closeConversation(id);
      } catch {
        // non-fatal — local state is already reset either way
      }
    }
  }, [conversationId]);

  const value = useMemo(
    () => ({
      isOpen,
      openWidget,
      closeWidget,
      toggleWidget,
      messages,
      sending,
      streamingContent,
      toolCallActive: activeToolCalls > 0,
      initializing,
      dailyMessageLimit,
      dailyLimitReached,
      conversationMode,
      sendMessage,
      clearConversation,
      closeConversation,
    }),
    [
      isOpen,
      openWidget,
      closeWidget,
      toggleWidget,
      messages,
      sending,
      streamingContent,
      activeToolCalls,
      initializing,
      dailyMessageLimit,
      dailyLimitReached,
      conversationMode,
      sendMessage,
      clearConversation,
      closeConversation,
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return ctx;
}
